// Phase 15 M3 — BUYER first-purchase confirmation, web port.
//
// Mirrors mobile/src/screens/BuyerConfirmation.tsx (Phase 12 M8) without
// reusing any React Native code. Captures the per-sale SEPA + REGA + fiscal
// data the backend needs before the M4 PaymentIntent off-session debit can
// run.
//
// Backend: `POST /api/v1/marketplace/sales/:id/buyer-confirm` (already
// implemented). 200 → continue to M4 (checkout). 4xx → render the `reason`
// string so the user can correct.
//
// KYC web flow is out of scope per PRD §M3 — if the user's `kyc_status` is
// not VERIFIED, the page surfaces a "Completa o KYC na app móbil" notice
// rather than blocking the form (the backend will reject the submission
// anyway, so the inline error stays the canonical failure path).

import axios from 'axios';
import { UserCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import InfoCallout from '../components/InfoCallout';
import { confirmBuyerSale } from '../api/marketplace';
import type { BuyerConfirmRequest, VatRegime } from '@leyrochain/shared';

const VAT_LABELS: Record<VatRegime, string> = {
  NONE: 'Sen IVE',
  PARTICULAR: 'Particular',
  AUTONOMO_REAGP: 'Autónomo REAGP',
  AUTONOMO_GENERAL: 'Autónomo réxime xeral',
  EMPRESA: 'Empresa',
};

const VAT_REGIMES: VatRegime[] = ['PARTICULAR', 'AUTONOMO_REAGP', 'AUTONOMO_GENERAL', 'EMPRESA'];

// Lote V-2 (F-026/F-028) — mapeo reason → copy accionable. Os novos
// motivos `cannot_buy_own_sale`, `sale_already_claimed`, `not_auction_winner`
// chegan desde a Fase 1 do refactor de buyer-confirmation.ts. Sen mapeo o
// usuario vería literalmente "sale_already_claimed" como erro.
const REASON_COPY: Record<string, string> = {
  cannot_buy_own_sale: 'Non podes mercar un animal que ti mesmo puxeches á venda.',
  sale_already_claimed: 'Este animal xa o reservou outro comprador.',
  not_auction_winner: 'Esta subasta non é para ti — só pode confirmar quen a gañou.',
  sale_not_listed: 'A venda xa non está aberta.',
  already_confirmed: 'Xa confirmaches esta compra. Vai ao paso de pagamento.',
  iban_invalid: 'O IBAN introducido non é válido. Revísao por favor.',
  no_buyer_role: 'A túa conta non ten o rol comprador activo.',
  kyc_not_verified: 'Falta verificar a túa identidade (KYC).',
};

function describeAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: string; reason?: string; message?: string }
      | undefined;
    const reason = data?.reason ?? '';
    return REASON_COPY[reason] ?? reason ?? data?.error ?? data?.message ?? err.message ?? fallback;
  }
  return fallback;
}

export default function BuyerConfirmationPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [iban, setIban] = useState('');
  const [destRegaCode, setDestRegaCode] = useState('ES');
  const [vatRegime, setVatRegime] = useState<VatRegime>('AUTONOMO_GENERAL');
  const [entityType, setEntityType] = useState<'PERSOA_FISICA' | 'PERSOA_XURIDICA'>(
    'PERSOA_FISICA',
  );
  const [legalName, setLegalName] = useState('');
  const [fiscalAddress, setFiscalAddress] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kycPending, setKycPending] = useState(false);

  const cleanedIban = iban.replace(/\s+/g, '').toUpperCase();
  const ibanLooksValid = /^[A-Z0-9]{14,34}$/.test(cleanedIban);
  const regaLooksValid = /^ES\d{12}$/.test(destRegaCode.trim());
  const requiresLegalName = entityType === 'PERSOA_XURIDICA';

  const canSubmit = useMemo(
    () =>
      !pending &&
      ibanLooksValid &&
      regaLooksValid &&
      fiscalAddress.trim().length > 0 &&
      (!requiresLegalName || legalName.trim().length > 0),
    [pending, ibanLooksValid, regaLooksValid, fiscalAddress, requiresLegalName, legalName],
  );

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setKycPending(false);
    setPending(true);
    const payload: BuyerConfirmRequest = {
      iban: cleanedIban,
      dest_rega_code: destRegaCode.trim().toUpperCase(),
      vat_regime: vatRegime,
      entity_type: entityType,
      fiscal_address: fiscalAddress.trim(),
      ...(requiresLegalName ? { legal_name: legalName.trim() } : {}),
    };
    try {
      await confirmBuyerSale(id, payload);
      navigate(`/marketplace/checkout/${id}`, { replace: true });
    } catch (err) {
      const msg = describeAxiosError(err, 'Non se puido completar a confirmación.');
      if (/kyc/i.test(msg)) {
        setKycPending(true);
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-extrabold text-rauxa">Primeira compra</h1>
      <p className="text-sm text-muted mt-1">
        Antes de pagar precisamos os datos fiscais e o destino do animal. Estes datos quedan
        asociados á túa conta para futuras compras.
      </p>

      {kycPending ? (
        <div
          role="alert"
          className="mt-5 p-4 rounded-lg bg-yellow-50 border border-warning text-warning text-sm"
        >
          <strong>Falta verificar a túa identidade.</strong> Completa o KYC dende a app móbil para
          poder finalizar a compra.{' '}
          <Link to="/marketplace" className="underline">
            Volver ao marketplace
          </Link>
          .
        </div>
      ) : null}

      <div className="mt-5">
        <InfoCallout
          storageKey="buyer-confirmation-data"
          defaultOpen={true}
          icon={<UserCheck size={18} className="text-rauxa" />}
          title="Datos necesarios antes de mercar"
          body="Antes da túa primeira compra necesitamos verificar: identidade fiscal (NIF), conta bancaria (IBAN para devolucións) e código REGA da explotación de destino. Só se piden unha vez — quedan gardados para futuras compras."
        />
      </div>

      <form onSubmit={onSubmit} className="space-y-4 mt-6" aria-label="buyer-confirmation">
        <label className="block">
          <span className="text-sm font-semibold text-dark">IBAN para SEPA</span>
          <input
            type="text"
            value={iban}
            onChange={(e) => setIban(e.target.value.toUpperCase())}
            inputMode="text"
            autoComplete="off"
            placeholder="ES1234 5678 9012 3456 7890"
            className="mt-1 w-full px-4 py-2 rounded-lg border border-border bg-white font-mono uppercase focus:outline-none focus:border-rauxa"
            required
          />
          {iban.length > 0 && !ibanLooksValid ? (
            <span className="text-xs text-danger mt-1">IBAN inválido — revisa o formato.</span>
          ) : null}
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-dark">Código REGA do destino</span>
          <input
            type="text"
            value={destRegaCode}
            onChange={(e) => setDestRegaCode(e.target.value.toUpperCase())}
            placeholder="ES012345678901"
            className="mt-1 w-full px-4 py-2 rounded-lg border border-border bg-white font-mono uppercase focus:outline-none focus:border-rauxa"
            maxLength={14}
            required
          />
          {destRegaCode.length > 2 && !regaLooksValid ? (
            <span className="text-xs text-danger mt-1">Formato: ES + 12 díxitos.</span>
          ) : null}
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-dark">Réxime fiscal</span>
          <select
            value={vatRegime}
            onChange={(e) => setVatRegime(e.target.value as VatRegime)}
            className="mt-1 w-full px-4 py-2 rounded-lg border border-border bg-white focus:outline-none focus:border-rauxa"
          >
            {VAT_REGIMES.map((v) => (
              <option key={v} value={v}>
                {VAT_LABELS[v]}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-dark">Tipo de entidade</legend>
          {(['PERSOA_FISICA', 'PERSOA_XURIDICA'] as const).map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-dark">
              <input
                type="radio"
                name="entity_type"
                value={opt}
                checked={entityType === opt}
                onChange={() => setEntityType(opt)}
              />
              <span>{opt === 'PERSOA_FISICA' ? 'Persoa física' : 'Persoa xurídica'}</span>
            </label>
          ))}
        </fieldset>

        {requiresLegalName ? (
          <label className="block">
            <span className="text-sm font-semibold text-dark">Razón social</span>
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              className="mt-1 w-full px-4 py-2 rounded-lg border border-border bg-white focus:outline-none focus:border-rauxa"
              required
            />
          </label>
        ) : null}

        <label className="block">
          <span className="text-sm font-semibold text-dark">Enderezo fiscal</span>
          <textarea
            value={fiscalAddress}
            onChange={(e) => setFiscalAddress(e.target.value)}
            rows={3}
            className="mt-1 w-full px-4 py-2 rounded-lg border border-border bg-white focus:outline-none focus:border-rauxa"
            required
          />
        </label>

        {error ? (
          <div
            role="alert"
            className="p-3 rounded-lg bg-red-50 border border-danger text-danger text-sm"
          >
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2 rounded-lg bg-[#2D6E1A] text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {pending ? 'Gardando…' : 'Confirmar e continuar ao pagamento'}
        </button>
      </form>
    </div>
  );
}
